import { IBlocksByTagsResult, OverrideSResult } from '@rhizome/pgtypes';
import { convertDBRowToSlateNode } from '@rhizome/shared-slate-utils';
import Link from 'next/link';
import React, { useState } from 'react';
import styled from 'styled-components';
import { colors, DarkenSlightly } from '../styles/themes';
import { usePrivacy } from '../utils/usePrivacy';
import DateDisplay from './DateDisplay';
import { ExcerptEditor } from './SlateEditor/ExcerptEditor';
import { ReadOnlyDisplay } from './SlateEditor/usedPlugins';

/*
isRoot prop tells it whether or not it should be editable (if it is part of a popup, it should not be editable)
page tells it the react-query page number it is a part of, so it is able to update the correct cache page
*/
function ExcerptDisplay(
  props: OverrideSResult<IBlocksByTagsResult> & {
    page: number;
    isRoot: boolean;
  }
) {
  const title = props.note_title || 'untitled';
  const privateLink =
    '/notes/editor/' + props.slate_parent + '#' + props.block_id;

  const publicLink =
    '/post/' + props.slate_parent + '/' + '?excerpt=' + props.block_id;

  const [editing, setEditing] = useState(false);

  const isPrivate = usePrivacy() === 'private';
  const canEdit = props.isRoot && isPrivate;
  return (
    <ExcerptWrapper>
      <TitleRow>
        <TitleText>
          {' '}
          <Link href={isPrivate ? privateLink : publicLink}>
            {"from '" + title + "'"}
          </Link>
        </TitleText>
        {canEdit ? (
          editing ? (
            <EditingSign>editing</EditingSign>
          ) : (
            <EditButton
              className="ExcerptDisplayEditButton"
              onClick={() => {
                setEditing(true);
              }}
            >
              edit
            </EditButton>
          )
        ) : null}
      </TitleRow>

      <SubtitleText>
        <DateDisplay
          created={new Date(props.creation_date)}
          lastEdited={new Date(props.last_edited_date)}
          default="lastEdited"
        />
      </SubtitleText>
      {/* only allow editing if its root and private */}
      {editing && canEdit ? (
        <div
          tabIndex={0}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event?.relatedTarget as Node)) {
              setEditing(false);
            }
          }}
        >
          <ExcerptEditor {...props} page={props.page} />
        </div>
      ) : (
        <ReadOnlyDisplay
          nodes={[convertDBRowToSlateNode(props)]}
          isRoot={true}
        />
      )}
    </ExcerptWrapper>
  );
}

const SubtitleText = styled.div`
  padding: 0px 0px 0px 0px;
  margin: 0px;
  font-style: italic;
  font-size: 0.75rem;
`;

const TitleRow = styled.div`
  height: 1.5rem;
  margin-bottom: 0.3rem;
`;
const TitleText = styled.p`
  padding: 0px 0px 0px 0px;
  margin: 0px;
  font-size: 1.5rem;
  display: inline-block;
`;
const EditButton = styled.button`
  display: none;

  font-weight: bold;
  height: 100%;
  vertical-align: bottom;
  margin-left: 10px;

  border: none;
  color: ${colors.lightOffBlack};
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.main.medium};
  :hover {
    background-color: ${(props) => props.theme.colors.main.dark};
  }
  :hover {
    cursor: pointer;
  }
`;

const EditingSign = styled(EditButton)`
  display: inline-block;
  :hover {
    cursor: default;
    background-color: ${(props) => props.theme.colors.main.medium};
  }
`;

const ExcerptWrapper = styled.div`
  border: none;
  border-bottom: 1px solid black;
  padding-top: 25px;
  padding-bottom: 25px;

  :hover {
    background-color: ${DarkenSlightly};
    .ExcerptDisplayEditButton {
      display: inline-block;
    }
  }
`;

export default ExcerptDisplay;
